import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import Layout from "../components/Layout";
import { useLang } from "../contexts/LanguageContext";
import { PROJECT_IMAGES } from "../i18n/translations";

const Lightbox = ({ images, index, onClose, onPrev, onNext }) => {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") onPrev();
      else if (e.key === "ArrowRight") onNext();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose, onPrev, onNext]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center"
      onClick={onClose}
      data-testid="lightbox-overlay"
    >
      <button
        type="button"
        onClick={onClose}
        data-testid="lightbox-close"
        className="absolute top-6 right-6 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition z-10"
        aria-label="Close"
      >
        <X className="w-5 h-5" />
      </button>

      {images.length > 1 && (
        <>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onPrev(); }}
            data-testid="lightbox-prev"
            className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition z-10"
            aria-label="Previous"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onNext(); }}
            data-testid="lightbox-next"
            className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition z-10"
            aria-label="Next"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        </>
      )}

      <motion.img
        key={index}
        initial={{ opacity: 0, scale: 0.98 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
        src={images[index]}
        alt=""
        onClick={(e) => e.stopPropagation()}
        className="max-w-[92vw] max-h-[90vh] object-contain select-none"
        data-testid="lightbox-image"
      />

      {images.length > 1 && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/70 text-xs uppercase tracking-[0.2em]">
          {index + 1} / {images.length}
        </div>
      )}
    </motion.div>
  );
};

const Projetos = () => {
  const { t } = useLang();
  const projects = t.projectsData.filter((p) => !p.hidden);
  const [lightbox, setLightbox] = useState(null); // { slug, index }

  const openLightbox = useCallback((slug, index) => {
    setLightbox({ slug, index });
  }, []);

  const closeLightbox = useCallback(() => setLightbox(null), []);

  const activeImages = lightbox ? PROJECT_IMAGES[lightbox.slug] || [] : [];

  const prev = useCallback(() => {
    setLightbox((lb) =>
      lb ? { ...lb, index: (lb.index - 1 + activeImages.length) % activeImages.length } : lb
    );
  }, [activeImages.length]);

  const next = useCallback(() => {
    setLightbox((lb) =>
      lb ? { ...lb, index: (lb.index + 1) % activeImages.length } : lb
    );
  }, [activeImages.length]);

  return (
    <Layout>
      <section className="pt-32 pb-16 md:pt-40 md:pb-24" data-testid="projetos-intro">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <span className="uppercase tracking-[0.28em] text-xs text-[#666]">{t.projetos.eyebrow}</span>
          <h1
            className="mt-6 font-light tracking-tight text-4xl sm:text-5xl md:text-6xl max-w-3xl leading-[1.1]"
            style={{ fontFamily: "Outfit, sans-serif" }}
            data-testid="projetos-title"
          >
            {t.projetos.title}
          </h1>
          <p className="mt-6 text-lg md:text-xl text-[#444] max-w-2xl leading-relaxed">
            {t.projetos.subtitle}
          </p>
        </div>
      </section>

      <section className="pb-24 md:pb-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 space-y-20 md:space-y-28">
          {projects.map((p, idx) => {
            const imgs = PROJECT_IMAGES[p.slug] || [];
            return (
              <motion.article
                key={p.slug}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.8 }}
                className={`grid md:grid-cols-12 gap-6 md:gap-10 items-center ${
                  idx % 2 === 1 ? "md:[&>*:first-child]:order-2" : ""
                }`}
                data-testid={`projeto-${p.slug}`}
              >
                <div className="md:col-span-8 space-y-3">
                  <button
                    type="button"
                    onClick={() => openLightbox(p.slug, 0)}
                    className="block w-full relative overflow-hidden aspect-[4/3] group cursor-zoom-in"
                    data-testid={`projeto-${p.slug}-main-img`}
                    aria-label={`Expand ${p.name}`}
                  >
                    <img
                      src={imgs[0]}
                      alt={p.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1.2s]"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
                  </button>
                  {imgs.length > 1 && (
                    <div className="grid grid-cols-4 gap-3">
                      {imgs.slice(1).map((img, i) => (
                        <button
                          key={i}
                          type="button"
                          onClick={() => openLightbox(p.slug, i + 1)}
                          data-testid={`projeto-${p.slug}-thumb-${i}`}
                          className="relative aspect-square overflow-hidden group cursor-zoom-in"
                          aria-label={`Expand photo ${i + 2}`}
                        >
                          <img
                            src={img}
                            alt={`${p.name} ${i + 2}`}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                          />
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/15 transition-colors" />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div className="md:col-span-4">
                  <p className="uppercase tracking-[0.28em] text-[11px] text-[#666]">0{idx + 1} · {p.status}</p>
                  <h2
                    className="mt-4 font-light tracking-tight text-3xl md:text-4xl"
                    style={{ fontFamily: "Outfit, sans-serif" }}
                  >
                    {p.name}
                  </h2>
                  <dl className="mt-6 space-y-3 text-sm">
                    <div className="flex justify-between border-b border-[#E5E5E5] pb-2">
                      <dt className="text-[#666]">{t.projetos.locationLabel}</dt>
                      <dd className="text-[#1C1C1C] text-right">{p.location}</dd>
                    </div>
                    <div className="flex justify-between border-b border-[#E5E5E5] pb-2">
                      <dt className="text-[#666]">{t.projetos.typologyLabel}</dt>
                      <dd className="text-[#1C1C1C] text-right">{p.typology}</dd>
                    </div>
                    <div className="flex justify-between border-b border-[#E5E5E5] pb-2">
                      <dt className="text-[#666]">{t.projetos.statusLabel}</dt>
                      <dd className="text-[#1C1C1C] text-right">{p.status}</dd>
                    </div>
                  </dl>
                  <p className="mt-6 text-[#444] leading-relaxed">{p.description}</p>
                  <Link
                    to="/contactos"
                    data-testid={`projeto-${p.slug}-cta`}
                    className="mt-8 inline-flex items-center gap-2 border-b border-[#1C1C1C] pb-1 text-xs uppercase tracking-[0.2em] hover:gap-4 transition-all"
                  >
                    {t.nav.cta}
                  </Link>
                </div>
              </motion.article>
            );
          })}
        </div>
      </section>

      <AnimatePresence>
        {lightbox && (
          <Lightbox
            images={activeImages}
            index={lightbox.index}
            onClose={closeLightbox}
            onPrev={prev}
            onNext={next}
          />
        )}
      </AnimatePresence>
    </Layout>
  );
};

export default Projetos;
